import {ReactNode} from "react";
import {ListItem} from "components/common/list/ListItem.component";
import UserProfile from "components/workflows/UserProfile.component";
import {useTranslation} from "components/translations/useTranslation";
import {UserDTO} from "types/userDTO";
import {WorkflowHistoryEventProps} from "types/newWorkflowDetails";
import {formatDateTime} from "./workflowDetails.function";

const APPROVAL_APPLICATION = "Visma.net Approval";

// One row of the former workflow history table, rendered as a list item inside its step
export function WorkflowHistoryEvent({event, uniqueKey, listedUnderStep}: Readonly<WorkflowHistoryEventProps>) {
    const {translate} = useTranslation();
    const application = event.displayApplication || event.application;
    const isFinished = event.action === "APPROVED" || event.action === "REJECTED";

    const user = (userDTO: UserDTO | undefined, key: string, fallbackName?: string) => userDTO ?
        <UserProfile user={userDTO} uniqueKey={`${uniqueKey}-${key}`}/> :
        <span className="workflow-event__user-name">{fallbackName}</span>;

    const actor = user(event.user, "user", event.fullUserName);

    const renderActorLine = (): ReactNode => {
        switch (event.action) {
            case "SENT_FOR_REVIEW":
            case "FORWARDED":
                return <>
                    {actor}
                    {event.toUser && <>
                        <span>{translate(`taskDetail.historyEvent.${event.action}`)}</span>
                        {user(event.toUser, "to-user")}
                    </>}
                </>;
            case "REVIEWED":
                return <>{actor}<span>{translate("taskDetail.historyEvent.REVIEWED")}</span></>;
            case "REASSIGNED":
                return <>
                    {actor}
                    {event.originalAssignee && <>
                        <span>{translate("taskDetail.historyEvent.REASSIGNED")}</span>
                        {user(event.originalAssignee, "original-assignee")}
                    </>}
                    {event.toUser && <>
                        <span>{translate("taskDetail.toLower")}</span>
                        {user(event.toUser, "to-user")}
                    </>}
                </>;
            default:
                return <>
                    {actor}
                    {event.substitute && event.originalAssignee && <>
                        <span>({translate("taskDetail.onBehalfOf")}</span>
                        {user(event.originalAssignee, "original-assignee")}
                        <span>)</span>
                    </>}
                </>;
        }
    }

    const comment = event.comment ? <span className="workflow-event__comment">{event.comment}</span> : null;
    const detail = (text: string) => <span className="workflow-event__comment">{text}</span>;

    let title: string;
    let data: Array<ReactNode>;
    switch (event.eventType) {
        case "START":
            title = translate("taskDetail.activated");
            data = [
                <span key="actor" className="workflow-event__actor">{actor}</span>,
                comment,
                detail(`${translate("taskDetail.fromUpper")}: ${application}`),
            ];
            break;
        case "END":
            title = translate("taskDetail.returned");
            data = [
                <span key="actor" className="workflow-event__actor">
                    <span className="workflow-event__user-name">
                        {isFinished ? APPROVAL_APPLICATION : application}
                    </span>
                </span>,
                isFinished ? detail(`${translate("taskDetail.to")}: ${application}`) : null,
                event.action === "CANCELED" ? detail(translate("taskDetail.actionTypes.CANCELED")) : null,
            ];
            break;
        default:
            title = translate(`taskDetail.actionTypes.${event.action}`);
            // The step it names is no longer in the workflow, so it is listed under another one
            if (listedUnderStep !== undefined && event.stepName && event.stepName !== listedUnderStep) {
                title = `${title} (${event.stepName})`;
            }
            data = [
                <span key="actor" className="workflow-event__actor">{renderActorLine()}</span>,
                comment,
                event.action === "DOC_CHANGED" ? detail(`${translate("taskDetail.fromUpper")}: ${application}`) : null,
            ];
    }

    return (
        <ListItem className="workflow-event"
                  dataCy="workflowDetails.historyEvent"
                  title={title}
                  data={data.filter(Boolean)}
                  description={formatDateTime(event.date)}/>
    );
}

export default WorkflowHistoryEvent;
